import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import saApi from "../lib/saApi";
import {
  Loader2, Eye, ChevronRight, ShieldOff, Building2, Clock, RotateCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const METHOD_COLORS = {
  GET: "text-emerald-300 bg-emerald-500/10",
  POST: "text-cyan-300 bg-cyan-500/10",
  PUT: "text-amber-300 bg-amber-500/10",
  PATCH: "text-amber-300 bg-amber-500/10",
  DELETE: "text-red-300 bg-red-500/10",
};

function shortPath(p) {
  if (!p) return "—";
  // Trim /api/ prefix for readability.
  return p.replace(/^\/api\//, "");
}

function ago(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return d.toLocaleString();
}

export default function SAImpersonationLog() {
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(null); // {sa_id, tenant_id, sa_email, tenant_name}
  const [events, setEvents] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const loadSessions = async () => {
    try {
      const { data } = await saApi.get("/impersonate/sessions");
      setSessions(data.items || []);
    } catch { setSessions([]); }
  };
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingEvents(true);
    saApi.get("/impersonate/timeline", { params: { tenant_id: selected.tenant_id, sa_id: selected.sa_id, limit: 20 } })
      .then(({ data }) => setEvents(data.items || []))
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [selected]);

  return (
    <SuperAdminShell
      title="Session Replay Timeline"
      actions={
        <Button data-testid="sa-timeline-refresh" variant="ghost" onClick={loadSessions}
                className="text-white/70 hover:text-white hover:bg-white/5">
          <RotateCw className="w-4 h-4 mr-1.5" />Refresh
        </Button>
      }
    >
      <div className="px-5 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Sessions list */}
        <div className="rounded-2xl border border-white/10 backdrop-blur-md overflow-hidden"
             style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-white/50">
            Recent Impersonation Sessions
          </div>
          {sessions === null ? (
            <div className="px-4 py-6 text-white/50 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />Loading…
            </div>
          ) : sessions.length === 0 ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">
              <Eye className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No impersonation activity yet.
              <div className="text-[11px] text-white/30 mt-1">Traffic logs appear here on every request.</div>
            </div>
          ) : (
            <ul>
              {sessions.map((s) => {
                const active = selected && selected.sa_id === s.sa_id && selected.tenant_id === s.tenant_id;
                return (
                  <li key={`${s.sa_id}-${s.tenant_id}`}>
                    <button
                      data-testid={`sa-timeline-session-${s.tenant_id}`}
                      onClick={() => setSelected(s)}
                      className={`w-full text-left px-4 py-3 border-b border-white/5 flex items-start gap-3 transition ${active ? "bg-cyan-400/10" : "hover:bg-white/5"}`}
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                           style={{ background: "rgba(34,211,238,0.15)" }}>
                        <Building2 className="w-4 h-4 text-cyan-300" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm font-medium truncate">{s.tenant_name}</div>
                        <div className="text-[11px] text-white/60 truncate font-mono">{s.sa_email}</div>
                        <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ago(s.last_ts)}</span>
                          <span>· {s.events} events</span>
                          {s.blocked > 0 && (
                            <span className="text-red-300 flex items-center gap-1">
                              <ShieldOff className="w-3 h-3" />{s.blocked} blocked
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 mt-1.5 transition ${active ? "text-cyan-300" : "text-white/30"}`} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Event list */}
        <div className="rounded-2xl border border-white/10 backdrop-blur-md overflow-hidden"
             style={{ background: "rgba(255,255,255,0.02)" }}>
          {!selected ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm h-full flex items-center justify-center flex-col">
              <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Select a session on the left to view the last 20 pages visited.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{selected.tenant_name}</div>
                  <div className="text-[11px] text-white/50 truncate font-mono">{selected.sa_email}</div>
                </div>
                <Badge className="bg-white/10 text-white/70 border-0 shrink-0">Last 20 events</Badge>
              </div>
              {loadingEvents ? (
                <div className="px-4 py-6 text-white/50 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />Loading events…
                </div>
              ) : events?.length === 0 ? (
                <div className="px-6 py-10 text-white/40 text-sm text-center">No events in the rolling window.</div>
              ) : (
                <ol className="divide-y divide-white/5">
                  {events?.map((e) => (
                    <li key={e.id} data-testid="sa-timeline-event"
                        className="px-4 py-2.5 flex items-center gap-3">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${METHOD_COLORS[e.method] || "text-white/60 bg-white/10"}`}>
                        {e.method}
                      </span>
                      <span className="text-sm text-white/90 flex-1 min-w-0 truncate font-mono">
                        {shortPath(e.path)}
                      </span>
                      {e.blocked ? (
                        <Badge className="bg-red-500/15 text-red-300 border-0 text-[10px]">
                          <ShieldOff className="w-3 h-3 mr-1 inline" />Blocked
                        </Badge>
                      ) : (
                        <span className={`text-xs font-mono ${e.status >= 400 ? "text-red-300" : "text-emerald-300"}`}>
                          {e.status}
                        </span>
                      )}
                      <span className="text-[10px] text-white/40 uppercase tracking-widest shrink-0 w-20 text-right">
                        {ago(e.ts)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
      </div>
    </SuperAdminShell>
  );
}
